import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';
import ProduccionGuiaPdfButton from '../../../components/Produccion/ProduccionGuiaPdfButton';
import { useCanMutate } from '../../../hooks/useCanMutate';
import produccionGuiaService from '../../../services/produccionGuiaService';
import { useModuleSettings } from '../../../contexts/ModuleSettingsContext';
import { getGuiaConfigBySlug } from '../../../utils/produccionGuiaPaths';

const GuiasProduccionList = () => {
  const { tipoSlug } = useParams();
  const navigate = useNavigate();
  const config = getGuiaConfigBySlug(tipoSlug);
  const { produccionLineasEnabled } = useModuleSettings();
  const canMutate = useCanMutate();

  const [rows, setRows] = useState([]);
  const [lineas, setLineas] = useState([]);
  const [lineFilter, setLineFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pdfProgress, setPdfProgress] = useState('');

  const basePath = `/produccion/guias/${tipoSlug}`;

  const loadList = useCallback(async () => {
    if (!config) return;
    setLoading(true);
    try {
      const params = { guide_type: config.guideType };
      if (search.trim()) params.search = search.trim();
      if (produccionLineasEnabled && lineFilter !== 'all') {
        params.produccion_linea_id = lineFilter;
      }
      const res = await produccionGuiaService.list(params);
      setRows(res.success && Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setRows([]);
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al cargar guías',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [config, search, produccionLineasEnabled, lineFilter]);

  useEffect(() => {
    if (!produccionLineasEnabled) return;
    produccionGuiaService.formOptions().then((res) => {
      if (res.success && res.data?.lineas) setLineas(res.data.lineas);
    }).catch(() => {});
  }, [produccionLineasEnabled]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  if (!config) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <p className="text-red-600">Tipo de guía no válido.</p>
        <Link to="/produccion/guias" className="text-teal-700 text-sm mt-2 inline-block hover:underline">
          Volver
        </Link>
      </div>
    );
  }

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      const res = await produccionGuiaService.remove(deleteTarget.id);
      if (res.success) {
        setToast({ isOpen: true, message: 'Guía eliminada', type: 'success' });
        setDeleteOpen(false);
        setDeleteTarget(null);
        await loadList();
      } else {
        setToast({ isOpen: true, message: res.message || 'No se pudo eliminar', type: 'error' });
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al eliminar',
        type: 'error',
      });
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">
          Dashboard
        </Link>
        <span className="mx-2">/</span>
        <Link to="/produccion" className="hover:text-teal-600">
          Producción
        </Link>
        <span className="mx-2">/</span>
        <Link to="/produccion/guias" className="hover:text-teal-600">
          Guías de producción
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">{config.shortTitle}</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{config.title}</h1>
          <p className="text-sm text-gray-600 mt-1">{config.description}</p>
        </div>
        {canMutate ? (
          <button
            type="button"
            onClick={() => navigate(`${basePath}/nueva`)}
            className="shrink-0 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-sm font-medium"
          >
            Nueva guía
          </button>
        ) : null}
      </div>

      <div className="mb-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm flex flex-wrap gap-4 items-end">
        <div>
        <label htmlFor="guia-buscar" className="block text-xs font-medium text-gray-600 mb-1">
          Buscar por código
        </label>
        <input
          id="guia-buscar"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Ej. GPT-20260528"
          className="w-full max-w-xs rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500"
        />
        </div>
        {produccionLineasEnabled && (
          <div>
            <label htmlFor="guia-linea-filtro" className="block text-xs font-medium text-gray-600 mb-1">
              Línea de producción
            </label>
            <select
              id="guia-linea-filtro"
              value={lineFilter}
              onChange={(e) => setLineFilter(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 min-w-[12rem]"
            >
              <option value="all">Todas las líneas</option>
              {lineas.map((linea) => (
                <option key={linea.id} value={linea.id}>
                  {linea.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
        {loading ? (
          <p className="p-8 text-center text-gray-600 text-sm">Cargando…</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Código</th>
                <th className="text-left px-4 py-3 font-medium whitespace-nowrap">Fecha / Hora</th>
                <th className="text-left px-4 py-3 font-medium">Almacén</th>
                <th className="text-left px-4 py-3 font-medium">Proyecto / Operación</th>
                {produccionLineasEnabled ? (
                  <th className="text-left px-4 py-3 font-medium">Línea</th>
                ) : null}
                <th className="text-left px-4 py-3 font-medium">Recetas</th>
                <th className="text-right px-4 py-3 font-medium"> </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={produccionLineasEnabled ? 7 : 6} className="px-4 py-8 text-center text-gray-500">
                    No hay guías registradas.
                    {canMutate ? (
                      <>
                        {' '}
                        <button
                          type="button"
                          onClick={() => navigate(`${basePath}/nueva`)}
                          className="text-teal-700 font-medium hover:underline"
                        >
                          Crear la primera
                        </button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-gray-900">{row.code}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {row.guide_date}
                      {row.guide_time ? ` ${row.guide_time}` : ''}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{row.warehouse_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {row.project_name || '—'}
                      {row.project_type === 'operacion' ? (
                        <span className="ml-1 text-xs text-gray-500">(operación)</span>
                      ) : null}
                    </td>
                    {produccionLineasEnabled ? (
                      <td className="px-4 py-3 text-gray-700">{row.produccion_linea_name || '—'}</td>
                    ) : null}
                    <td className="px-4 py-3 text-gray-700">{row.items_with_qty_count}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {row.items_with_qty_count > 0 ? (
                        <>
                          <button
                            type="button"
                            onClick={() => navigate(`${basePath}/analisis/${row.id}`)}
                            className="text-teal-800 hover:text-teal-950 font-medium mr-3"
                          >
                            Costos
                          </button>
                          <Link
                            to={`${basePath}/imprimir/${row.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-600 hover:text-gray-900 font-medium mr-3"
                            title="Vista imprimible"
                          >
                            Imprimir
                          </Link>
                          <span className="inline-flex align-middle mr-3">
                            <ProduccionGuiaPdfButton
                              guiaId={row.id}
                              onProgress={setPdfProgress}
                            onSuccess={() => {
                              setPdfProgress('');
                              setToast({ isOpen: true, message: 'PDF descargado', type: 'success' });
                            }}
                            onError={(msg) => {
                              setPdfProgress('');
                              setToast({ isOpen: true, message: msg || 'Error al generar PDF', type: 'error' });
                            }}
                            />
                          </span>
                        </>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => navigate(`${basePath}/editar/${row.id}`)}
                        className="text-teal-700 hover:text-teal-900 font-medium mr-3"
                      >
                        {canMutate ? 'Editar' : 'Ver'}
                      </button>
                      {canMutate ? (
                        <button
                          type="button"
                          onClick={() => {
                            setDeleteTarget(row);
                            setDeleteOpen(true);
                          }}
                          className="text-red-600 hover:text-red-800 font-medium"
                        >
                          Borrar
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-4 text-sm">
        <Link to="/produccion/guias" className="text-teal-700 font-medium hover:underline">
          ← Todas las guías de producción
        </Link>
      </div>

      <ConfirmModal
        isOpen={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleDelete}
        title="Eliminar guía"
        message={
          deleteTarget
            ? `¿Eliminar la guía ${deleteTarget.code}? Esta acción no se puede deshacer.`
            : ''
        }
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
      />

      {pdfProgress ? (
        <p className="mb-3 text-xs text-teal-800 bg-teal-50 border border-teal-200 rounded-md px-3 py-2">
          {pdfProgress}
        </p>
      ) : null}

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </div>
  );
};

export default GuiasProduccionList;
